import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { ChevronDown, SettingsAdjust } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Button } from 'bones/design-system/components/Button';
import { MenuItems } from 'bones/design-system/components/select/MenuItems';
import { Tooltip } from 'bones/design-system/components/Tooltip';
import { Body } from 'bones/design-system/components/typography/Body';
import { getLocalName } from 'bones/utils/getLocalName';
import { isNotEqual } from 'bones/utils/isEqual';

export interface VoiceboxChatSettingsDividerProps {
  isInitial?: boolean;
  messagesRef: React.RefObject<HTMLDivElement>;
  prevSettings?: VoiceboxSettings;
  settings: VoiceboxSettings;
}

export const VoiceboxChatSettingsDivider: React.VFC<
  VoiceboxChatSettingsDividerProps
> = ({ isInitial, messagesRef, prevSettings, settings }) => {
  const { copy, testIds } = useVoiceboxSchema();

  return (
    <div
      className={classNames(
        'vb-voicebox-chat-divider',
        'vb-voicebox-chat-indicator'
      )}
    >
      <div className="vb-voicebox-chat-divider-line" />
      <Tooltip
        boundary={messagesRef.current || /* istanbul ignore next */ undefined}
        minimal
        popoverClassName={classNames(
          'vb-voicebox-chat-divider-tooltip',
          `vb-popover-position--${BlueprintPopoverPosition.BOTTOM}`
        )}
        portalContainer={
          (process.env.NODE_ENV === 'test' &&
            document.getElementById('vet-bones-root')) ||
          /* istanbul ignore next */ undefined
        }
        position={BlueprintPopoverPosition.BOTTOM}
        slot={
          <MenuItems className="vb-voicebox-chat-divider-content">
            <li>
              <Body size="md" weight="light">
                {copy.message.divider.content.database.label}
              </Body>
              <Body
                className="vb-voicebox-chat-divider-value"
                ellipsize
                size="md"
                weight={
                  !prevSettings ||
                  prevSettings.databaseId !== settings.databaseId
                    ? 'regular'
                    : 'light'
                }
              >
                {settings.databaseId}
              </Body>
            </li>
            <li>
              <Body size="md" weight="light">
                {copy.message.divider.content.model.label}
              </Body>
              <Body
                className="vb-voicebox-chat-divider-value"
                ellipsize
                size="md"
                weight={
                  !prevSettings || prevSettings.model !== settings.model
                    ? 'regular'
                    : 'light'
                }
              >
                {settings.model}
              </Body>
            </li>
            <li>
              <Body size="md" weight="light">
                {copy.message.divider.content.namedGraphs.label}
              </Body>
              <Body
                className="vb-voicebox-chat-divider-value"
                ellipsize
                size="md"
                weight={
                  !prevSettings ||
                  isNotEqual(prevSettings.namedGraphs, settings.namedGraphs)
                    ? 'regular'
                    : 'light'
                }
              >
                {settings.namedGraphs
                  .map((graph) => getLocalName(graph))
                  .join(', ')}
              </Body>
            </li>
            <li>
              <Body size="md" weight="light">
                {copy.message.divider.content.reasoning.label}
              </Body>
              <Body
                className="vb-voicebox-chat-divider-value"
                ellipsize
                size="md"
                weight={
                  !prevSettings || prevSettings.reasoning !== settings.reasoning
                    ? 'regular'
                    : 'light'
                }
              >
                {settings.reasoning
                  ? copy.message.divider.content.reasoning.enabled
                  : copy.message.divider.content.reasoning.disabled}
              </Body>
            </li>
          </MenuItems>
        }
      >
        <Button
          className="vb-voicebox-chat-divider-button"
          leftIcon={SettingsAdjust}
          rightIcon={ChevronDown}
          size="xs"
          testId={testIds.messages.divider.button}
          variant="tertiary-gray"
        >
          {isInitial
            ? copy.message.divider.button.initial
            : copy.message.divider.button.updated}
        </Button>
      </Tooltip>
      <div className="vb-voicebox-chat-divider-line" />
    </div>
  );
};
